import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Boxes, Building2, List, Search } from 'lucide-react'
import { catalogApi, type ClientInput, type ClientRow } from '@/api/catalogApi'
import { NativeSelect } from '@/components/common/Field'
import { NO_PRODUCT, clientProducts, filterByProduct, groupByProduct, productOptions } from '@/lib/clientProducts'
import { cn } from '@/lib/utils'
import { Page } from '@/components/layout/Page'
import { PageHeading } from '@/components/layout/PageHeading'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ListState, StatusPill, SuperAdminGate, tdClass, thClass } from '@/components/admin/catalog/CatalogShared'
import { ClientForm } from '@/components/admin/catalog/ClientForm'
import { useCatalogClients, useCatalogMutation } from '@/hooks/useCatalog'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { EmptyState } from '@/components/common/States'

/** /admin/clients — clients (Customer) with tenant counts (superadmin). */
export default function CatalogClientsPage() {
  return (
    <SuperAdminGate>
      <ClientsList />
    </SuperAdminGate>
  )
}

function ClientsList() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const debounced = useDebouncedValue(q.trim(), 250)
  const clients = useCatalogClients(debounced)
  const [creating, setCreating] = useState(false)
  const create = useCatalogMutation((input: ClientInput) => catalogApi.createClient(input))

  // Product filter and grouping live in the URL so a view can be linked.
  const [params, setParams] = useSearchParams()
  const product = params.get('product')
  const grouped = params.get('group') === 'product'
  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next, { replace: true })
  }

  const all = clients.data ?? []
  const options = productOptions(all)
  const shown = filterByProduct(all, product)

  return (
    <Page>
      <PageHeading
        description={t('catalog.eyebrow')}
        title={t('catalog.clients.title')}
        actions={
          <>
            <div role="radiogroup" aria-label={t('catalog.clients.grouping')} className="inline-flex rounded-md border border-line-strong p-0.5">
              {([
                [false, List, 'list'],
                [true, Boxes, 'byProduct'],
              ] as const).map(([value, Icon, key]) => (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={grouped === value}
                  onClick={() => setParam('group', value ? 'product' : null)}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-[5px] px-2.5 py-1.5 text-meta',
                    grouped === value ? 'bg-accent font-medium text-accent-foreground' : 'text-ink-muted hover:text-ink',
                  )}
                >
                  <Icon className="size-4" aria-hidden />
                  {t(`catalog.clients.view.${key}`)}
                </button>
              ))}
            </div>
            <NativeSelect
              aria-label={t('catalog.clients.product')}
              className="w-48"
              value={product ?? ''}
              onChange={(e) => setParam('product', e.target.value || null)}
            >
              <option value="">{t('catalog.clients.allProducts')}</option>
              {options.map((o) => (
                <option key={o.key} value={o.key}>
                  {t('catalog.clients.productOption', { name: o.key === NO_PRODUCT ? t('catalog.clients.noProduct') : o.name, count: o.count })}
                </option>
              ))}
              {product && !options.some((o) => o.key === product) ? <option value={product}>{product}</option> : null}
            </NativeSelect>
            <div className="relative w-64">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-muted" />
              <Input aria-label={t('catalog.clients.search')} placeholder={t('catalog.clients.search')} className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <Button onClick={() => setCreating(true)}>{t('catalog.clients.new')}</Button>
          </>
        }
      />
      <ListState query={clients}>
        {shown.length === 0 ? (
          <EmptyState compact icon={Building2} message={t('catalog.clients.empty')} />
        ) : grouped ? (
          <div className="flex flex-col gap-6">
            {groupByProduct(shown).map((g) => (
              <section key={g.product?.key ?? NO_PRODUCT} aria-label={g.product?.name ?? t('catalog.clients.noProduct')}>
                <h2 className="mb-2 flex items-baseline gap-2 text-body font-semibold text-ink-strong">
                  {g.product?.name ?? t('catalog.clients.noProduct')}
                  <span className="text-meta font-normal text-ink-muted">{t('catalog.clients.groupCount', { count: g.clients.length })}</span>
                </h2>
                <ClientsTable clients={g.clients} />
              </section>
            ))}
          </div>
        ) : (
          <ClientsTable clients={shown} />
        )}
      </ListState>

      <Dialog
        open={creating}
        onOpenChange={(o) => {
          if (!o) {
            setCreating(false)
            create.reset()
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('catalog.clients.createTitle')}</DialogTitle>
          </DialogHeader>
          {creating ? (
            <ClientForm
              pending={create.isPending}
              error={create.error}
              onCancel={() => setCreating(false)}
              onSubmit={(input) => create.mutate(input, { onSuccess: (c) => navigate(`/admin/clients/${c.id}?tab=tenants`) })}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Page>
  )
}

/** The clients table: one row per client, its products as chips. */
function ClientsTable({ clients }: { clients: ClientRow[] }) {
  const { t } = useTranslation()
  return (
    <table className="w-full table-fixed border-collapse">
      <thead className="bg-surface-subtle">
        <tr>
          <th className={thClass}>{t('catalog.clients.cols.name')}</th>
          <th className={`${thClass} w-72`}>{t('catalog.clients.cols.products')}</th>
          <th className={`${thClass} w-44`}>{t('catalog.clients.cols.slug')}</th>
          <th className={`${thClass} w-40`}>{t('catalog.clients.cols.timeZone')}</th>
          <th className={`${thClass} w-24`}>{t('catalog.clients.cols.tenants')}</th>
          <th className={`${thClass} w-28`}>{t('catalog.clients.cols.status')}</th>
        </tr>
      </thead>
      <tbody>
        {clients.map((c) => (
          <tr key={c.id} className="border-b border-line hover:bg-surface-subtle">
            <td className={`${tdClass} font-medium text-ink-strong`}>
              <Link to={`/admin/clients/${c.id}`} className="hover:underline">
                {c.name}
              </Link>
              {c.emailDomains.length ? <p className="truncate font-mono text-label font-normal text-ink-muted">{c.emailDomains.join(', ')}</p> : null}
            </td>
            <td className={tdClass}>
              <ProductChips client={c} />
            </td>
            <td className={`${tdClass} truncate font-mono`}>{c.slug}</td>
            <td className={tdClass}>{c.timeZone}</td>
            <td className={`${tdClass} font-mono`}>{c._count.tenants}</td>
            <td className={tdClass}>
              <StatusPill status={c.status} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** "CGT · Prod, Validation" per product; links to the client's environments tab. */
function ProductChips({ client }: { client: ClientRow }) {
  const { t } = useTranslation()
  const products = clientProducts(client)
  if (products.length === 0) {
    return (
      <Link to={`/admin/clients/${client.id}?tab=environments`} className="text-label text-ink-muted hover:underline">
        {t('catalog.clients.noProduct')}
      </Link>
    )
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {products.map((p) => (
        <Link
          key={p.key}
          to={`/admin/clients/${client.id}?tab=environments`}
          title={p.environments.map((e) => `${e.name} · ${t(`catalog.kinds.${e.kind}`)}`).join('\n')}
          className="inline-flex max-w-full items-center gap-1 truncate rounded-sm border border-line bg-surface-subtle px-2 py-[3px] text-label hover:border-line-strong"
        >
          <span className="font-medium text-ink-strong">{p.name}</span>
          <span className="truncate text-ink-muted">· {p.environments.map((e) => e.name).join(', ')}</span>
        </Link>
      ))}
    </div>
  )
}
